import { formatAmount } from '@40seas/formatters';
import { Currency } from '@40seas/models';
import { Box } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
    BarPlot,
    BarSeriesType,
    ChartContainer,
    ChartsClipPath,
    ChartsGrid,
    ChartsXAxis,
    ChartsYAxis,
    useDrawingArea,
    useXScale,
    useYScale,
    XAxis,
    YAxis,
} from '@mui/x-charts';
import get from 'lodash/get';
import { useCallback, useId, useMemo } from 'react';
import { BarTooltip } from './BarTooltip';
import { EmptyBarChart } from './EmptyBarChart';
import { BarChartProps } from './types';

export const BarChart = ({
    color = 'primary',
    data = [],
    height,
    currency,
    seriesConfig: initialSeriesConfig = [{ key: 'value' }],
    horizontal = false,
    gap = 0.2,
    borderRadius = 1,
    hideAxis = false,
    margin = { top: 0, right: 0, bottom: 0, left: 0 },
    roundStacks = false,
    emptyText,
    isLoading,
}: BarChartProps) => {
    const clipPathId = useId();
    const theme = useTheme();

    const getColor = useCallback(
        (colorValue: string) => get(theme.palette, [colorValue, 'main']) || get(theme.palette, colorValue) || colorValue,
        [theme.palette],
    );

    const series = useMemo(() => {
        const seriesConfig =
            initialSeriesConfig && initialSeriesConfig.length > 0 ? initialSeriesConfig : [{ key: 'value', color: color || 'primary' }];

        return seriesConfig.map((keyConfig) => {
            const seriesData = data.map((item) => item[keyConfig.key] ?? 0).filter((value) => typeof value === 'number') as number[];
            const seriesColor = keyConfig.color ? getColor(keyConfig.color) : getColor(color);

            return {
                data: seriesData,
                type: 'bar' as const,
                valueFormatter: (value: number | null) => (value && formatAmount({ value, currency })) || '',
                color: seriesColor,
                label: keyConfig.label,
                stack: keyConfig.stack,
                ...(horizontal && { layout: 'horizontal' as const }),
            } as BarSeriesType;
        });
    }, [color, currency, data, getColor, horizontal, initialSeriesConfig]);

    const valueFormatter = useMemo(() => continuousAxisValueFormatter(series, currency), [series, currency]);

    const xAxisDefinition: XAxis[] = useMemo(() => {
        if (horizontal) {
            return [
                {
                    scaleType: 'linear' as const,
                    ...calculateContinuousAxisRange(series, data.length, false),
                    valueFormatter,
                    height: hideAxis ? 0 : undefined,
                },
            ];
        }
        return [
            {
                scaleType: 'band' as const,
                data: data.map((d) => d.label),
                categoryGapRatio: gap,
            },
        ];
    }, [horizontal, series, data, valueFormatter, hideAxis, gap]);

    const yAxisDefinition: YAxis[] = useMemo(() => {
        if (horizontal) {
            return [
                {
                    scaleType: 'band' as const,
                    data: data.length === 1 ? [''] : data.map((d) => d.label),
                    categoryGapRatio: gap,
                    width: hideAxis ? 0 : undefined,
                },
            ];
        }
        const highestValue = Math.max(0, ...series.flatMap((s) => s.data as number[]));
        const width = 10 + (valueFormatter(highestValue).length || 4) * 7;
        return [
            {
                ...calculateContinuousAxisRange(series, data.length, true),
                valueFormatter: (value: number) => {
                    if (isLoading) return '';
                    return valueFormatter(value);
                },
                width,
            },
        ];
    }, [horizontal, series, data, valueFormatter, hideAxis, gap, isLoading]);

    const hasData = useMemo(() => series.some((s) => s.data?.some((d) => d !== 0)), [series]);

    if (!hasData && !isLoading) {
        return <EmptyBarChart {...{ emptyText, height }} />;
    }

    return (
        <ChartContainer
            sx={{
                '& .MuiBarElement-root:hover': {
                    opacity: 0.6,
                    cursor: 'pointer',
                },
                '& .MuiChartsAxis-line, & .MuiChartsAxis-tick, & .MuiChartsGrid-line': {
                    stroke: `${theme.palette.dividerDark} !important`,
                },
                '& .MuiChartsAxis-tick': {
                    strokeDasharray: '4 4',
                },
            }}
            series={series}
            xAxis={xAxisDefinition}
            yAxis={yAxisDefinition}
            height={height}
            margin={margin}
        >
            {isLoading && <LoadingOverlay />}
            {!hideAxis && <ChartsGrid horizontal={!horizontal} vertical={horizontal} strokeDasharray="4 4" />}
            <ChartsClipPath id={clipPathId} />
            {!isLoading && (
                <Box
                    component="g"
                    clipPath={`url(#${clipPathId})`}
                    sx={{
                        ...(roundStacks && {
                            '& clipPath rect': { clipPath: `inset(0px round ${theme.shape.borderRadius * borderRadius}px) !important` },
                        }),
                    }}
                >
                    <BarPlot series={series} borderRadius={theme.shape.borderRadius * borderRadius} />
                </Box>
            )}
            {!hideAxis && <ChartsXAxis disableTicks={!horizontal} tickPlacement="middle" />}
            {!hideAxis && <ChartsYAxis disableTicks={!!horizontal} tickPlacement="middle" />}
            <BarTooltip orientation={horizontal ? 'horizontal' : 'vertical'} series={series} data={data} />
        </ChartContainer>
    );
};

const continuousAxisValueFormatter = (series: BarSeriesType[], currency: Currency) => (value: number) => {
    if (value === 0 && series.flatMap((s) => s.data as number[]).every((v) => v === 0)) return '';
    if (value === 0 && series.flatMap((s) => s.data as number[]).some((v) => v !== 0)) return '0';

    const symbol = getCurrencySymbol(currency);
    const numValue = Number(value);
    if (isNaN(numValue)) return '';

    if (Math.abs(numValue) >= 1_000_000) {
        return `${symbol}${(numValue / 1_000_000).toFixed(1)}M`;
    }
    if (Math.abs(numValue) >= 1000) {
        return `${symbol}${(numValue / 1000).toFixed(0)}k`;
    }
    return `${symbol}${numValue.toFixed(0)}`;
};

function getCurrencySymbol(currencyCode: Currency): string | null {
    const formatter = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const formatted = formatter.format(0);
    return formatted.replace(/[\d\s,.]/g, '');
}

const calculateContinuousAxisRange = (seriesInput: BarSeriesType[], numCategories: number, roundMax: boolean = true) => {
    if (!seriesInput || seriesInput.length === 0 || numCategories === 0) {
        const defaultTicks = [0, 1000, 2000, 3000, 4000];
        return { tickValues: defaultTicks.slice(1), min: 0, max: 4000 };
    }

    let calculatedMaxValue = 0;
    if (numCategories > 0) {
        const categoryMaxValues: number[] = [];
        for (let i = 0; i < numCategories; i++) {
            const sumsByStackGroup: { [stackId: string]: number } = {};
            const individualBarValues: number[] = [];

            seriesInput.forEach((s) => {
                const value = s.data && i < s.data.length ? s.data[i] : 0;
                if (s.stack) {
                    sumsByStackGroup[s.stack] = (sumsByStackGroup[s.stack] || 0) + (value || 0);
                } else {
                    individualBarValues.push(Math.abs(value || 0));
                }
            });

            const maxStackSumForCategory =
                Object.keys(sumsByStackGroup).length > 0 ? Math.max(0, ...Object.values(sumsByStackGroup).map((sum) => Math.abs(sum))) : 0;

            const maxIndividualBarForCategory = individualBarValues.length > 0 ? Math.max(0, ...individualBarValues) : 0;

            categoryMaxValues.push(Math.max(maxStackSumForCategory, maxIndividualBarForCategory));
        }
        calculatedMaxValue = categoryMaxValues.length > 0 ? Math.max(0, ...categoryMaxValues) : 0;
    }

    const maxValue = calculatedMaxValue;

    if (maxValue === 0) {
        const allRawFlattenedValues = seriesInput.flatMap((s) => s.data || []);
        if (allRawFlattenedValues.length === 0 || allRawFlattenedValues.every((v) => v === 0)) {
            const defaultTicks = [0, 1000, 2000, 3000, 4000];
            return { tickValues: defaultTicks.slice(1), min: 0, max: 4000 };
        }
    }

    const numTicks = 5;
    let niceMax: number;

    if (!roundMax) {
        niceMax = maxValue;
    } else {
        niceMax = maxValue;

        if (maxValue > 0) {
            const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
            niceMax = Math.ceil(maxValue / magnitude) * magnitude;

            if (niceMax <= maxValue) {
                if (magnitude / 2 > 0 && niceMax < maxValue + magnitude / 4) {
                    niceMax = Math.ceil(maxValue / (magnitude / 2)) * (magnitude / 2);
                }
                if (niceMax <= maxValue) {
                    niceMax = Math.ceil(maxValue / magnitude + 0.5) * magnitude;
                }
                if (niceMax === 0 && maxValue > 0) {
                    niceMax = magnitude;
                }
            }

            if (niceMax < maxValue * 1.05 && niceMax > 0) {
                const step = magnitude / (niceMax / magnitude >= 5 ? 2 : 1);
                niceMax = Math.ceil(maxValue / step) * step;
                if (niceMax <= maxValue) niceMax += step;
            }

            if (niceMax === maxValue && maxValue !== 0) {
                niceMax += magnitude / (numCategories > 1 && niceMax / magnitude > 1 ? 2 : 1);
            }
            if (niceMax === 0 && maxValue > 0) {
                niceMax = Math.ceil(maxValue * 1.2);
                if (niceMax < 10) niceMax = Math.ceil(niceMax);
                else if (niceMax < 1) niceMax = parseFloat(niceMax.toFixed(1));
            }
        } else {
            niceMax = 4000;
        }

        if (niceMax === 0 && maxValue > 0) {
            if (maxValue < 10) niceMax = Math.ceil(maxValue * 1.2) || 10;
            else if (maxValue < 100) niceMax = Math.ceil(maxValue / 10) * 10 || 10;
            else niceMax = Math.pow(10, Math.floor(Math.log10(maxValue)) + 1);

            if (niceMax === 0) niceMax = 10;
        }
    }

    const tickStep = niceMax > 0 ? niceMax / (numTicks - 1) : 1000;

    const reasonableTickStep = niceMax > 0 && tickStep < 1 && tickStep !== 0 ? parseFloat(tickStep.toPrecision(1)) : Math.round(tickStep);

    const originalTickValues = Array.from({ length: numTicks }, (_, i) => {
        const val = i * reasonableTickStep;

        return niceMax < 10 && niceMax > 0 ? parseFloat(val.toFixed(2)) : Math.round(val);
    });

    const finalTickValues = originalTickValues.length > 0 ? originalTickValues.slice(1) : [];
    return { tickValues: finalTickValues, min: 0, max: niceMax > 0 ? niceMax : 4000 };
};

const LoadingOverlay = () => {
    const xScale = useXScale<'band'>();
    const yScale = useYScale();

    const { left, width, height } = useDrawingArea();

    const [bottom, top] = yScale.range();

    return (
        <g>
            {'bandwidth' in xScale &&
                xScale.domain().map((item, index) => {
                    const ratios = [0.2, 0.8, 0.6, 0.5];
                    const ratio = ratios[index % ratios.length];
                    const barHeight = ratio * (bottom - top);

                    return (
                        <LoadingRect key={index} x={xScale(item)} width={xScale.bandwidth()} y={bottom - barHeight} height={barHeight} />
                    );
                })}
            <LoadingText x={left + width / 2} y={top + height / 2}>
                Loading data…
            </LoadingText>
        </g>
    );
};

const LoadingRect = styled('rect')(({ theme }) => ({
    opacity: 0.2,
    fill: theme.palette.text.disabled,
}));

const LoadingText = styled('text')(({ theme }) => ({
    stroke: 'none',
    fill: theme.palette.text.primary,
    shapeRendering: 'crispEdges',
    textAnchor: 'middle',
    dominantBaseline: 'middle',
}));
